<template>
  <div class="video-card-block">
    <a class="card-link" :title="title" :href="episodeUrl()">
      <div v-if="episodesCount" class="series-label">
        {{ seriesLabel }}
      </div>
      <img class="thumbnail" :alt="title" :src="thumbnail" />

      <div v-if="progress_bar" class="progress-wrapper">
        <EpisodeProgressComponent :progress-bar="progress_bar" />
      </div>

      <div v-if="sequenceNumber && seriesSlug" class="episode-label">
        {{ `${EPISODE_PREFIX} ${sequenceNumber}` }}
      </div>

      <span class="card-title" :class="{ 'card-title__light': isLight }">
        {{ title }}
      </span>
    </a>
    <span class="card-title-alt" :class="{ 'card-title-alt__light': isLight }">
      {{ getSubTitle() }}
    </span>

    <SpecialEpisodeLabels :episode="getSpecialLabelsData()" :is-search="true" />

    <div class="topics-block">
      <span
        v-for="topic in topics"
        :key="topic.id"
        class="topic"
        :class="{ topic__light: isLight }"
      >
        <a class="topic-link" :title="topic.name" :href="getTopicUrl(topic)">
          {{ topic.name }}
        </a>
      </span>
    </div>
  </div>
</template>

<script>
import SpecialEpisodeLabels from '../../../shared/components/SpecialEpisodeLabels'
import EpisodeProgressComponent from '../../../shared/components/EpisodeProgress'

export default {
  name: 'VideoCard',
  components: { EpisodeProgressComponent, SpecialEpisodeLabels },
  props: {
    duration: {
      required: false,
      default: 0,
      type: Number,
    },
    title: {
      required: true,
      default: '',
      type: String,
    },
    titleAlt: {
      required: false,
      default: '',
      type: String,
    },
    thumbnail: {
      required: true,
      default: '',
      type: String,
    },
    slug: {
      required: true,
      default: '',
      type: String,
    },
    seriesSlug: {
      required: false,
      default: '',
      type: String,
    },
    topics: {
      required: false,
      default: () => [],
      type: Array,
    },
    episodesCount: {
      required: true,
      default: 0,
      type: Number,
    },
    sequenceNumber: {
      required: true,
      default: 0,
      type: Number,
    },
    exclusive: {
      required: true,
      default: false,
      type: Boolean,
    },
    premiering: {
      required: true,
      default: false,
      type: Boolean,
    },
    liveAtLabel: {
      required: false,
      default: '',
      type: String,
    },
    countdownTooltip: {
      required: false,
      default: '',
      type: String,
    },
    episodeProgressBars: {
      required: false,
      default: () => [],
      type: Array,
    },
    settings: {
      required: true,
      default: () => ({
        watch_host: '',
        main_host: '',
        events_host: '',
      }),
      type: Object,
    },
    isLight: {
      required: true,
      default: true,
      type: Boolean,
    },
    seriesLabel: {
      required: false,
      default: '',
      type: String,
    },
  },
  data() {
    return {
      EPISODE_PREFIX: 'Episode',
      progress_bar: null,
    }
  },
  mounted() {
    if (!this.episodesCount) this.fetchProgressBar()
  },
  methods: {
    getBaseUrl() {
      const protocol = window.location.protocol
      const watchHost = this.settings.watch_host

      return `${protocol}//${watchHost}`
    },
    episodeUrl() {
      const type = this.episodesCount ? 'series' : 'single-episode'
      const seriesSlug = this.seriesSlug || type

      return `${this.getBaseUrl()}/${seriesSlug}/${this.slug}`
    },
    getTopicUrl(topic) {
      return `${this.getBaseUrl()}/topic/${topic.slug}`
    },
    getSpecialLabelsData() {
      return {
        exclusive: this.exclusive,
        premiering: this.premiering,
        live_at_label: this.liveAtLabel,
        countdown_tooltip: this.countdownTooltip,
      }
    },
    getSubTitle() {
      if (this.seriesSlug) return this.titleAlt
      if (!this.seriesSlug && !this.episodesCount) return 'Single'

      return `Series • ${this.episodesCount} episodes`
    },
    fetchProgressBar() {
      const userId = localStorage.getItem('current_user')
      if (!userId) return

      const rawProgressBar = this.episodeProgressBars.find((pb) => {
        return pb.user_id === parseInt(userId)
      })

      if (rawProgressBar) {
        this.progress_bar = {
          percent: this.matchPercent(rawProgressBar),
          start_time_seconds: rawProgressBar.start_time_seconds,
        }
      }
    },
    matchPercent(progressBar) {
      let percent =
        (parseFloat(progressBar.start_time_seconds) / this.duration) * 100
      if (percent < 1) percent = 1
      return percent.toFixed(2)
    },
  },
}
</script>

<style lang="scss" scoped>
.video-card-block {
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 200px;
  max-width: 270px;
  flex: 1;

  .thumbnail {
    height: 140px;
    width: 100%;
    object-fit: cover;
    font-family: 'object-fit: cover;', serif;
    margin-bottom: 8px;
  }
}

.video-card__content {
  display: flex;
  flex-direction: column;
}

.card-title {
  color: #fff;
  font-size: 16px;
  font-weight: 500;
  line-height: 20px;
  font-family: 'DM Sans', sans-serif !important;
  cursor: pointer;
}

.card-title__light {
  color: #323232;
}

.card-title-alt {
  margin-top: 4px;
  color: #a6a6a6;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: -0.01em;
  font-family: 'DM Sans', sans-serif !important;
  white-space: break-spaces;
}

.card-title-alt__light {
  color: #5c5c5c;
}

.topics-block {
  margin-top: 8px;
  display: flex;
  flex-direction: row;
  width: 100%;
  flex-wrap: wrap;
  gap: 6px;
}

.topic {
  padding: 4px 10px 4px;
  font-size: 12px;
  line-height: 16px;
  font-family: 'DM Sans', serif;
  font-style: normal;
  font-weight: 400;
  background-color: #3b3946;
  color: #d1d3d4;
  border: 1px solid #444050;
  box-sizing: border-box;
  border-radius: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
}

.topic__light {
  color: #5c5c5c;
  background-color: #fff;
  border: 1px solid #d1d3d4;
}

.card-link,
.topic-link {
  color: inherit;
  white-space: break-spaces;
  cursor: pointer;
  line-height: 0;
}

.series-label {
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 0 0 4px 0;
  background: #b56941;
  color: #fff;
  text-align: center;
  font-feature-settings: 'clig' off, 'liga' off;
  font-family: DM Sans, serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 16px;
  padding: 4px 12px;
}

.episode-label {
  position: absolute;
  top: 110px;
  width: 100%;
  height: 30px;
  background: rgba(50, 145, 147, 0.5);
  color: #fff;
  font-family: DM Sans, serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 2.24px;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
}

.progress-wrapper {
  position: absolute;
  top: 136px;
  width: 100%;
}
</style>
